<template>
    <div>
        <GlobalPopover v-for="(value, index) in popovers"
            :key="value.uid"
            :popoverIndex="`${index}`"
            :isControllerSpawned="true"
            :startupPopoverConfig="value"
            @onClose="onCloseSpecificPopover(value, index)" />
    </div>
</template>
<script>
import { guuid, isFunction } from '@aretheyhappy/utils';
import GlobalPopover from './GlobalPopover.vue';

/**
 * Consumers are only meant to mount one mead-global-popover-controller "topmost on the page",
 * but a page combining several independent demos (as Storybook's docs pages do) can end up
 * with several. Rather than every mounted controller rendering its own copy of each popover,
 * the controllers share a single set of Flash subscriptions and each popover is routed to
 * exactly one of them - the controller nearest in the DOM to the popover's trigger element.
 * That keeps popovers inside the same container as their trigger, which matters when an
 * ancestor (e.g. a transformed Storybook story block) scopes `position: fixed`.
 */
const DISPATCHER_UID = 'mead-global-popover-controller-dispatcher';

/** Mounted controllers, in the order they were created */
const controllers = [];

/** Controller that owns each open popover, in the order they were opened */
const openStack = [];

/**
 * Find the controller closest in the DOM to the popover's trigger element, by walking up from
 * the trigger until reaching an ancestor that contains a controller. Falls back to the first
 * mounted controller when there's no trigger element or none share an ancestor with it.
 * @param {PopoverConfig} config
 */
function findOwner (config) {
    const trigger = config.target || (config.event || {}).currentTarget || false;
    if (trigger instanceof Node) {
        for (let node = trigger; node; node = node.parentNode) {
            const match = controllers.find(controller => controller.$el && node.contains(controller.$el));
            if (match) return match;
        }
    }
    return controllers[0];
}

function onOpen (config) {
    const owner = findOwner(config);
    if (!owner) return;
    openStack.push(owner);
    owner.addPopover(config);
}

function onClosedInternal () {
    const owner = openStack.pop();
    if (owner) owner.removePopoverInternal();
}

function onClosedService () {
    const owner = openStack.pop();
    if (owner) owner.removePopoverService();
}

/**
 * Remove the most recent open popover entry owned by the given controller
 * @param controller
 */
function removeFromOpenStack (controller) {
    const index = openStack.lastIndexOf(controller);
    if (index !== -1) openStack.splice(index, 1);
}

export default {
    name: 'mead-global-popover-controller',
    components: {GlobalPopover},
    data: function () {
        return {
            uid: guuid(),
            popovers: []
        }
    },
    methods: {
        addPopover: function (popoverConfig) {
            MeadCommon.FrontendLogger.info('GlobalPopoverController.addPopover');
            this.popovers.push(popoverConfig);
        },
        removePopoverInternal: function () {
            MeadCommon.FrontendLogger.info('GlobalPopoverController.removePopoverInternal');
            this.popovers.pop();
        },
        onCloseSpecificPopover(popover, index) {
            MeadCommon.FrontendLogger.info('GlobalPopoverController.onCloseSpecificPopover', popover, index);
            if (isFunction(popover.next)) popover.next();
            this.popovers.splice(index, 1);
            removeFromOpenStack(this);
        },
        removePopoverService: function () {
            MeadCommon.FrontendLogger.info('GlobalPopoverController.removePopoverService');
            // Call next function if defined
            if (this.popovers.length > 0 && isFunction(this.popovers[this.popovers.length-1].next)) {
                this.popovers[this.popovers.length-1].next();
            }
            this.popovers.pop();
        }
    },
    created: function () {
        /** The first controller sets up the shared subscriptions that route to all of them */
        if (controllers.length === 0) {
            MeadCommon.Flash.subscribe(DISPATCHER_UID, MeadCommon.PopoverServiceEvents.POPOVER_OPEN, onOpen);
            MeadCommon.Flash.subscribe(DISPATCHER_UID, MeadCommon.PopoverServiceEvents.POPOVER_CLOSED_INTERNAL, onClosedInternal);
            MeadCommon.Flash.subscribe(DISPATCHER_UID, MeadCommon.PopoverServiceEvents.POPOVER_CLOSED_SERVICE, onClosedService);
        }
        controllers.push(this);
    },
    destroyed() {
        const index = controllers.indexOf(this);
        if (index !== -1) controllers.splice(index, 1);
        for (let i = openStack.length - 1; i >= 0; i--) {
            if (openStack[i] === this) openStack.splice(i, 1);
        }
        if (controllers.length === 0) MeadCommon.Flash.unsubscribe(DISPATCHER_UID);
    },
}
</script>
